import React from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, BookOpen, Play, History, MoreHorizontal, Pin } from 'lucide-react';
import { SkeletonTable, EmptyState, ErrorState } from '@/components/ui';
import { Tooltip } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { WorkspaceToolbar } from '@/components/layout/WorkspaceToolbar';
import type { ReportTemplate } from '@/components/reports/ReportCard';

interface ReportsTableProps {
  reports: ReportTemplate[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedReport: string | null;
  onSelectReport: (reportId: string) => void;
  onRun?: (reportId: string) => void;
  onOpenHistory?: (reportId: string) => void;
  /** @deprecated List/grid toggle removed; list view only. */
  viewMode?: 'list' | 'grid';
  /** @deprecated List/grid toggle removed. */
  onViewModeChange?: (mode: 'list' | 'grid') => void;
  /** Optional pagination; omit for single long page. */
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  totalCount: number;
  onNavigateToMethodology?: () => void;
  /** When true, omit WorkspaceToolbar and outer main; render only catalog content for embedding in a Panel. */
  embedded?: boolean;
  /** When report needs_baseline and this is null, Run is disabled. */
  baselineSnapshot?: { id: string } | null;
  /** Pinned report template IDs (persisted in localStorage by parent). */
  pinnedReportIds?: Set<string>;
  /** Toggle pin for a report by id. */
  onPinToggle?: (reportId: string) => void;
}

export const ReportsTable: React.FC<ReportsTableProps> = ({
  reports,
  loading,
  error,
  searchQuery,
  onSearchChange,
  selectedReport,
  onSelectReport,
  onRun,
  onOpenHistory,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  totalCount,
  onNavigateToMethodology,
  embedded = false,
  baselineSnapshot = null,
  pinnedReportIds,
  onPinToggle,
}) => {
  const showPagination = totalPages > 1 && onPageChange != null;

  const formatCategory = (category: string): string => {
    if (category.toLowerCase() === 'kpi') return 'KPI';
    return category.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  function statusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
    const s = (status ?? '').toLowerCase();
    if (s === 'completed') return 'default';
    if (s === 'failed') return 'destructive';
    if (s === 'running' || s === 'queued') return 'secondary';
    return 'outline';
  }

  if (error) {
    const wrapper = embedded ? 'div' : 'main';
    const Wrapper = wrapper;
    return (
      <Wrapper className="flex-1 flex flex-col min-w-0 bg-background">
        <ErrorState
          title="Error"
          description={error}
          onRetry={() => window.location.reload()}
        />
      </Wrapper>
    );
  }

  const tableAndPagination = (
    <>
      {/* Table Container */}
      <div className={embedded ? 'flex-1 overflow-auto px-4 py-3 min-h-0' : 'flex-1 overflow-auto p-6 bg-background'}>
        {loading ? (
          <SkeletonTable rows={6} cols={2} />
        ) : reports.length === 0 ? (
          <EmptyState
            description={
              searchQuery
                ? 'No reports match your search criteria.'
                : 'No report templates available. Report templates will appear here once loaded from the API.'
            }
          />
        ) : (
          <div className="border border-border rounded-lg overflow-hidden bg-card">
            <table className="w-full caption-bottom text-sm border-collapse">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="w-[50%] py-3 px-4 text-left align-middle font-medium text-muted-foreground">
                    <span className="text-xs font-semibold uppercase tracking-wider">Report Name</span>
                  </th>
                  <th className="py-3 px-4 text-left align-middle font-medium text-muted-foreground">
                    <span className="text-xs font-semibold uppercase tracking-wider">Category</span>
                  </th>
                  <th className="w-24 py-3 px-4 text-right align-middle font-medium text-muted-foreground">
                    <span className="text-xs font-semibold uppercase tracking-wider">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => {
                  const isSelected = selectedReport === report.id;
                  const isPinned = pinnedReportIds?.has(report.id) ?? false;
                  return (
                    <tr
                      key={report.id}
                      onClick={() => onSelectReport(report.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelectReport(report.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Select ${report.name} report`}
                      aria-pressed={isSelected}
                      className={cn(
                        'cursor-pointer border-b border-border transition-colors',
                        'hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                        isSelected ? 'bg-muted/50' : 'bg-card'
                      )}
                    >
                      <td className="py-2.5 px-4 align-middle">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {report.name}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {report.description || 'No description'}
                          </p>
                        </div>
                      </td>
                      <td className="py-2.5 px-4 align-middle">
                        <Badge variant="outline" className="bg-muted/50 text-secondary-foreground text-xs font-normal">
                          {formatCategory(report.category)}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-4 align-middle text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {onPinToggle && (
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn(
                                'h-7 w-7 p-0 border-border',
                                isPinned ? 'text-nb-accent border-nb-accent/50 bg-nb-accent/10' : ''
                              )}
                              onClick={() => onPinToggle(report.id)}
                              aria-label={isPinned ? 'Unpin report' : 'Pin report'}
                              aria-pressed={isPinned}
                            >
                              <Pin className={cn('h-4 w-4', isPinned && 'fill-nb-accent')} />
                            </Button>
                          )}
                          {onRun && (
                            report.needs_baseline && !baselineSnapshot ? (
                              <Tooltip content="A baseline must be uploaded and set for this project to run this report." position="bottom">
                                <span className="inline-block">
                                  <Button variant="default" size="sm" className="h-7 px-2 text-xs" disabled aria-label={`Run ${report.name}`}>
                                    <Play size={12} className="mr-1" aria-hidden />
                                    Run
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : (
                              <Button
                                variant="default"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRun(report.id);
                                }}
                                aria-label={`Run ${report.name}`}
                              >
                                <Play size={12} className="mr-1" aria-hidden />
                                Run
                              </Button>
                            )
                          )}
                          {(onOpenHistory || onRun) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 w-7 p-0 border-border"
                                  aria-label={`More actions for ${report.name}`}
                                >
                                  <MoreHorizontal size={14} aria-hidden />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-40">
                                {onRun && (
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onRun(report.id);
                                    }}
                                    disabled={report.needs_baseline === true && !baselineSnapshot}
                                  >
                                    <Play size={14} className="mr-2" aria-hidden />
                                    Run
                                  </DropdownMenuItem>
                                )}
                                {onOpenHistory && (
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onOpenHistory(report.id);
                                    }}
                                  >
                                    <History size={14} className="mr-2" aria-hidden />
                                    History
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Footer (only when multiple pages) */}
      {!loading && reports.length > 0 && (
        <div className={cn('flex-none px-6 py-3 border-t border-border flex items-center justify-between bg-card', embedded && 'px-4')}>
          <p className="text-xs text-muted-foreground">
            {showPagination ? (
              <>
                Showing <span className="font-medium text-foreground">
                  {((currentPage - 1) * 10) + 1}-{Math.min(currentPage * 10, totalCount)}
                </span> of <span className="font-medium text-foreground">{totalCount}</span> reports
              </>
            ) : (
              <>Showing all <span className="font-medium text-foreground">{totalCount}</span> report{totalCount === 1 ? '' : 's'}</>
            )}
          </p>
          {showPagination && onPageChange && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 h-9 border-border bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                <span className="sr-only">Previous page</span>
                ←
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="p-1 h-9 border-border bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                <span className="sr-only">Next page</span>
                →
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="flex flex-col flex-1 min-h-0 min-w-0" data-testid="reports-table-embedded">
        {tableAndPagination}
      </div>
    );
  }

  return (
    <main className="flex-1 flex flex-col min-w-0 bg-background">
      <WorkspaceToolbar
        title="Reports"
        subtitle={totalCount > 0 ? `${totalCount} report${totalCount === 1 ? '' : 's'}` : undefined}
        leftSlot={
          <div className="relative w-full max-w-sm">
            <Search className="absolute inset-y-0 left-0 flex items-center pl-3 h-9 text-muted-foreground group-focus-within:text-primary transition-colors" size={20} />
            <Input
              placeholder="Search reports by name..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full h-9 pl-10 pr-4 rounded-lg text-sm bg-muted/50 border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        }
        rightSlot={
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-9 text-muted-foreground hover:text-foreground hover:bg-muted border-border"
              onClick={() => onNavigateToMethodology?.()}
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Calculation Methodology
            </Button>
          </>
        }
      />
      {tableAndPagination}
    </main>
  );
};
